import { LOGO_BASE64 } from '@/utils/logoData';
import { GOOGLE_REVIEW_QR_BASE64, GOOGLE_REVIEW_URL } from '@/utils/googleReviewQrData';
import { printOrderViaQz, printDailyReportViaQz, isQzReady } from '@/utils/qzPrinter';

// Notify function — toast callback the admin page can pass in.
// Keeps this util free of UI deps.
let notify = null;
export const setPrintNotifier = (fn) => { notify = fn; };
const toast = (type, msg) => { if (notify) notify(type, msg); };

// ---------- Thermal formatting helpers ----------
// 80mm @ ~11pt Courier ≈ 42 cols, 58mm ≈ 32 cols.
const COLS_80 = 42;
const COLS_58 = 32;

const pad = (s, n) => (s + ' '.repeat(n)).slice(0, n);
const padL = (s, n) => (' '.repeat(n) + s).slice(-n);

// Escape HTML chars in dynamic text
const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Sentinel tokens used to inject <b>...</b> into the receipt text without
// rebuilding every fragment. The whole receipt body is HTML-escaped right
// before being placed in the <pre>; we then swap the sentinels for real
// tags so bold styling survives the escape pass.
const BOLD_BEG = '\x01';
const BOLD_END = '\x02';
const b = (s) => BOLD_BEG + s + BOLD_END;
const renderBold = (html) =>
  html.split(BOLD_BEG).join('<b>').split(BOLD_END).join('</b>');

// Two-column line: left text, right text pushed to col N
const row = (left, right, cols) => {
  const r = String(right);
  const l = String(left);
  if (l.length + 1 + r.length <= cols) {
    return pad(l, cols - r.length) + r;
  }
  // wrap left onto its own line, right on next line right-aligned
  const wrapped = wrap(l, cols);
  const last = wrapped[wrapped.length - 1];
  if (last.length + 1 + r.length <= cols) {
    wrapped[wrapped.length - 1] = pad(last, cols - r.length) + r;
    return wrapped.join('\n');
  }
  return wrapped.join('\n') + '\n' + padL(r, cols);
};

// Word-wrap to a column width
const wrap = (text, cols) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if ((cur + (cur ? ' ' : '') + w).length > cols) {
      if (cur) lines.push(cur);
      // very long word → hard split
      let rest = w;
      while (rest.length > cols) { lines.push(rest.slice(0, cols)); rest = rest.slice(cols); }
      cur = rest;
    } else {
      cur = cur ? cur + ' ' + w : w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
};

const centerLine = (s, cols) => {
  if (s.length >= cols) return s;
  const gap = Math.floor((cols - s.length) / 2);
  return ' '.repeat(gap) + s;
};

const hr = (cols, ch = '-') => ch.repeat(cols);

const money = (n) => `${Number(n || 0).toFixed(2)} EUR`;

// ---------- Item block (fixed-width, thermal-safe) ----------
// Layout (80mm / 42 cols):
//   1 BUTTER CHICKEN                   13.50
//     (Picante)
//
//   3 PAPADAM                           6.30
//
// Blank line between items for readability.
// Price shown as plain number (no EUR) — EUR only on totals.
const itemBlock = (item, cols) => {
  // Menu Infantil items print as "MENU INFANTIL [POLLO CON SALSA]"
  // so the kitchen knows it's a kids portion. Strip a leading
  // "Menu Infantil\n" from the display name (some kids items embed it
  // so the public menu card shows a two-line title) to avoid
  // duplicating the prefix.
  const isKids = (item.category || '').toLowerCase() === 'menu infantil';
  const cleanName = (item.name || '').replace(/^menu infantil\s*/i, '').trim();
  const rawName = esc(cleanName).toUpperCase();
  const name = isKids ? `MENU INFANTIL [${rawName}]` : rawName;
  const qty = String(item.quantity);
  const prefix = qty + ' ';
  const lineTotal = (item.price * item.quantity).toFixed(2);
  // Available width for name on first line = cols - prefix - space - price
  const firstAvail = cols - prefix.length - 1 - lineTotal.length;
  // Wrap the name into lines that fit firstAvail (first) and cols - indent (rest)
  const indent = ' '.repeat(prefix.length);
  const words = name.split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  let limit = firstAvail;
  for (const w of words) {
    const candidate = cur ? cur + ' ' + w : w;
    if (candidate.length <= limit) {
      cur = candidate;
    } else {
      if (cur) lines.push(cur);
      // hard-split very long word
      let rest = w;
      const nextLimit = cols - indent.length;
      while (rest.length > nextLimit) {
        lines.push(rest.slice(0, nextLimit));
        rest = rest.slice(nextLimit);
      }
      cur = rest;
      limit = nextLimit;
    }
  }
  if (cur) lines.push(cur);

  // First line gets qty prefix + price appended right-aligned
  const firstLineText = prefix + (lines[0] || '');
  let firstLine = pad(firstLineText, cols - lineTotal.length) + lineTotal;
  let continuationLines = lines.slice(1).map((l) => indent + l);
  // Bold the parenthesised variant in the name, e.g.
  //   "TIKKA MASALA (POLLO)" → "TIKKA MASALA " + bold("(POLLO)")
  // Applied AFTER padding so the price column stays aligned (the
  // sentinel tokens are stripped before they affect visible width).
  const boldParens = (s) => s.replace(/\(([^)]+)\)/g, BOLD_BEG + '($1)' + BOLD_END);
  firstLine = boldParens(firstLine);
  continuationLines = continuationLines.map(boldParens);

  // Variant dropdowns and notes — dropdowns bolded so kitchen spots them.
  const extras = [];
  if (item.spiciness) extras.push(indent + b('(' + esc(item.spiciness) + ')'));
  if (item.side_dish) extras.push(indent + b('(' + esc(item.side_dish) + ')'));
  if (item.notes) {
    const wrapped = wrap(esc(item.notes), cols - indent.length);
    extras.push(...wrapped.map((l) => indent + l));
  }

  // Blank line after each item for readability
  return [firstLine, ...continuationLines, ...extras, ''].join('\n');
};

// ---------- Payment method line ----------
const paymentText = (order, cols) => {
  if (order.payment_method !== 'whatsapp') return 'Tarjeta Online (Pagado)';
  if (order.payment_mode === 'cash') {
    if (order.cash_amount > 0) {
      return [
        'Efectivo',
        row('  Paga con:', money(order.cash_amount), cols),
        row('  Cambio:', money(order.cash_amount - order.total), cols),
      ].join('\n');
    }
    return 'Efectivo (Importe exacto)';
  }
  return 'Tarjeta al repartidor/recogida';
};

// ---------- Public: print order receipt ----------
// opts.width: '80mm' (default) or '58mm'
// Tries QZ Tray (raw ESC/POS) first; falls back to HTML browser print.
// Note: only uses QZ if already connected (isQzReady) — otherwise Chrome
// would block the fallback popup due to async gesture loss.
export const printOrderReceipt = (order, opts = {}) => {
  if (isQzReady()) {
    return printOrderViaQz(order, opts)
      .then(() => toast('success', 'Ticket enviado a la impresora'))
      .catch((err) => {
        toast('error', 'Error QZ Tray: ' + (err?.message || err));
        printOrderReceiptHtml(order, opts);
      });
  }
  return printOrderReceiptHtml(order, opts);
};

const printOrderReceiptHtml = (order, opts = {}) => {
  const width = opts.width === '58mm' ? '58mm' : '80mm';
  const cols = width === '58mm' ? COLS_58 : COLS_80;
  const bodyWidth = width === '58mm' ? '54mm' : '76mm';
  const pageWidth = width;

  const printWindow = window.open('', '_blank', 'width=380,height=720');
  if (!printWindow) return;

  const orderTypeText = order.order_type === 'pickup' ? 'RECOGIDA' : 'ENTREGA';
  const estTime = order.estimated_time || (order.order_type === 'pickup' ? '20 min' : '50 min');
  const subtotal = order.subtotal || order.total;
  const deliveryFee = order.delivery_fee || 0;
  const createdAt = new Date(order.created_at);
  const shortId = order.id.substring(0, 8).toUpperCase();

  const itemLines = order.items.map((i) => itemBlock(i, cols)).join('\n');

  const fiscal = opts.fiscalContext?.fiscal || null;
  const ticket = opts.fiscalContext?.ticket || null;
  const qrBase64 = opts.fiscalContext?.qrBase64 || null;

  const header = (fiscal && fiscal.nif)
    ? [
        hr(cols, '='),
        ...wrap(fiscal.razon_social || 'Taj Mahal', cols).map((l) => centerLine(l, cols)),
        centerLine('NIF: ' + fiscal.nif, cols),
        ...wrap(fiscal.direccion || 'Calle Real 109', cols).map((l) => centerLine(l, cols)),
        centerLine([fiscal.codigo_postal, fiscal.municipio].filter(Boolean).join(' ') + (fiscal.provincia ? ' (' + fiscal.provincia + ')' : ''), cols),
        centerLine('+34 602 704 745', cols),
        hr(cols, '='),
      ].join('\n')
    : [
        hr(cols, '='),
        centerLine('Calle Real 109', cols),
        centerLine('San Fernando, Cadiz', cols),
        centerLine('+34 602 704 745', cols),
        hr(cols, '='),
      ].join('\n');

  const ticketBanner = ticket
    ? [
        centerLine(ticket.verifactu_mode === 'verifactu' ? 'VERI*FACTU' : 'FACTURA SIMPLIFICADA', cols),
        centerLine(ticket.full_number, cols),
        hr(cols),
      ].join('\n')
    : '';

  const orderInfo = [
    row('PEDIDO:', '#' + shortId, cols),
    row('FECHA:', createdAt.toLocaleDateString('es-ES'), cols),
    row('HORA:', createdAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), cols),
    b(row('TIPO:', `${orderTypeText} (${estTime})`, cols)),
  ].join('\n');

  const customerBlock = [
    'CLIENTE',
    hr(cols),
    ...wrap('Nombre: ' + esc(order.customer_name || '-'), cols),
    ...wrap('Tel:    ' + esc(order.customer_phone || '-'), cols),
    ...(order.order_type === 'delivery' && order.customer_address
      ? wrap('Dir:    ' + esc(order.customer_address), cols)
      : []),
    ...(order.notes ? wrap('Notas:  ' + esc(order.notes), cols).map((l) => b(l)) : []),
  ].join('\n');

  const discAmt = Number(order.discount_amount || 0);
  const discPct = Number(order.discount_percentage || 0);
  const discSrc = order.discount_source || (order.discount_code ? 'code' : 'none');
  let discLabel = '';
  if (discAmt > 0) {
    discLabel = discSrc === 'pickup'
      ? `Recogida -${discPct.toFixed(0)}%:`
      : `Cod. ${order.discount_code || ''} -${discPct.toFixed(0)}%:`;
  }

  const totalsBlock = [
    row('Subtotal:', money(subtotal), cols),
    ...(discAmt > 0 ? [row(discLabel, '-' + money(discAmt), cols)] : []),
    ...(deliveryFee > 0 ? [row('Envio:', money(deliveryFee), cols)] : []),
    hr(cols, '='),
    row('TOTAL:', money(order.total), cols),
    hr(cols, '='),
    ...(ticket && ticket.vat ? [
      row(`Base imponible (IVA ${Number(ticket.vat.tipo).toFixed(0)}%):`, money(ticket.vat.base), cols),
      row('Cuota IVA:', money(ticket.vat.cuota), cols),
      hr(cols),
    ] : []),
  ].join('\n');

  const fiscalFooter = ticket
    ? [
        '',
        centerLine(ticket.verifactu_mode === 'verifactu' ? 'VERI*FACTU' : 'FACTURA SIMPLIFICADA', cols),
        centerLine('Verificable en la sede de la AEAT', cols),
        centerLine('Huella: ' + (ticket.current_hash || '').substring(0, 16) + '...', cols),
        hr(cols),
      ].join('\n')
    : '';

  // QR for AEAT validation (rendered as <img>, separate from <pre> block)
  const fiscalQrHtml = (ticket && qrBase64)
    ? `<div class="aeat-qr"><img src="data:image/png;base64,${qrBase64}" alt="QR AEAT"/><div class="aeat-qr-url">${esc(ticket.qr_url || '')}</div></div>`
    : '';

  const receiptText = [
    header,
    ticketBanner,
    orderInfo,
    hr(cols),
    customerBlock,
    hr(cols),
    'ARTICULOS',
    hr(cols),
    itemLines,
    hr(cols),
    totalsBlock,
    paymentText(order, cols),
    hr(cols),
    fiscalFooter,
    centerLine('Gracias por su pedido!', cols),
    centerLine('www.tajmahalsanfernando.es', cols),
  ].filter(Boolean).join('\n');

  const copies = Math.max(1, parseInt(opts.copies || 1, 10));
  const copyBlock = `<img class="logo" src="${LOGO_BASE64}" alt="Taj Mahal"/>
<pre>${renderBold(esc(receiptText))}</pre>
${fiscalQrHtml}
<div class="review-block">
  <div style="font-size:13px;margin-bottom:4px">Deja tu reseña en Google</div>
  <img class="review-qr" src="${GOOGLE_REVIEW_QR_BASE64}" alt="Google review QR"/>
  <div style="font-size:11px;margin-top:2px;word-break:break-all">${esc(GOOGLE_REVIEW_URL)}</div>
</div>
<div class="feed"></div>`;
  const body = Array(copies).fill(copyBlock).join('<div style="page-break-after:always"></div>');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Ticket #${shortId}</title><style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{background:#fff;color:#000}
  body{font-family:'Courier New','Courier',monospace;font-size:12px;line-height:1.25;width:${bodyWidth};margin:0 auto;padding:2mm 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:${pageWidth} auto;margin:2mm}
  @media print{body{width:${bodyWidth}}}
  .logo{display:block;margin:0 auto 4px auto;max-width:${bodyWidth}}
  pre{font-family:inherit;font-size:12px;line-height:1.3;white-space:pre;word-break:normal;margin:0}
  pre b{font-weight:700}
  .total-line{font-weight:bold;font-size:14px}
  .aeat-qr{text-align:center;margin:4px 0 8px 0}
  .aeat-qr img{width:30mm;height:30mm;display:block;margin:0 auto}
  .aeat-qr-url{font-size:8px;word-break:break-all;margin-top:2px;color:#333}
  .feed{height:30mm}
</style></head><body>
${body}
</body></html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = function () {
    printWindow.focus();
    setTimeout(function () { printWindow.print(); }, 400);
    setTimeout(function () { printWindow.close(); }, 4000);
  };
};

// ---------- Public: print daily sales report ----------
export const printDailySalesReport = (data, opts = {}) => {
  if (isQzReady()) {
    return printDailyReportViaQz(data, opts)
      .then(() => toast('success', 'Informe enviado a la impresora'))
      .catch((err) => {
        toast('error', 'Error QZ Tray: ' + (err?.message || err));
        printDailySalesReportHtml(data, opts);
      });
  }
  return printDailySalesReportHtml(data, opts);
};

const printDailySalesReportHtml = (data, opts = {}) => {
  const width = opts.width === '58mm' ? '58mm' : '80mm';
  const cols = width === '58mm' ? COLS_58 : COLS_80;
  const bodyWidth = width === '58mm' ? '54mm' : '76mm';
  const pageWidth = width;

  const printWindow = window.open('', '_blank', 'width=380,height=720');
  if (!printWindow) return;

  const dateFormatted = new Date(data.date).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const section = (title, lines) => [
    '',
    title,
    hr(cols),
    ...lines,
  ].join('\n');

  const reportText = [
    hr(cols, '='),
    centerLine('INFORME DE VENTAS DIARIO', cols),
    centerLine(dateFormatted, cols),
    hr(cols, '='),
    section('RESUMEN', [
      row('Total Pedidos:', String(data.total_orders), cols),
      row('Cancelados:', String(data.cancelled_orders), cols),
      hr(cols),
      row('INGRESOS:', money(data.total_revenue), cols),
    ]),
    section('POR CANAL', [
      row(`Web (${data.online_count}):`, money(data.online_revenue), cols),
      row(`WhatsApp (${data.whatsapp_count}):`, money(data.whatsapp_revenue), cols),
    ]),
    section('POR METODO DE PAGO', [
      row(`Tarjeta (${data.card_count}):`, money(data.card_revenue), cols),
      row(`Efectivo (${data.cash_count}):`, money(data.cash_revenue), cols),
    ]),
    section('POR TIPO', [
      row(`Entrega (${data.delivery_count}):`, money(data.delivery_revenue), cols),
      row(`Recogida (${data.pickup_count}):`, money(data.pickup_revenue), cols),
    ]),
    '',
    hr(cols, '='),
    centerLine('Generado: ' + new Date().toLocaleString('es-ES'), cols),
    centerLine('www.tajmahalsanfernando.es', cols),
  ].join('\n');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Informe Diario</title><style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{background:#fff;color:#000}
  body{font-family:'Courier New','Courier',monospace;font-size:12px;line-height:1.25;width:${bodyWidth};margin:0 auto;padding:2mm 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:${pageWidth} auto;margin:2mm}
  @media print{body{width:${bodyWidth}}}
  .logo{display:block;margin:0 auto 4px auto;max-width:${bodyWidth}}
  pre{font-family:inherit;font-size:12px;line-height:1.3;white-space:pre;word-break:normal;margin:0}
  .feed{height:30mm}
</style></head><body>
<img class="logo" src="${LOGO_BASE64}" alt="Taj Mahal"/>
<pre>${esc(reportText)}</pre>
<div class="feed"></div>
</body></html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = function () {
    printWindow.focus();
    setTimeout(function () { printWindow.print(); }, 400);
    setTimeout(function () { printWindow.close(); }, 3000);
  };
};
